<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import ImovelAPI from 'dashboard/api/imoveis';
import {
  buildTalkToTenantMessage,
  occupiedHouseNoticeDescriptor,
} from 'dashboard/helper/lais/visitaPresenter';
import { buildWhatsAppUrl } from '../utils/agendaQuickActions';
import OccupiedHouseTipsDialog from './OccupiedHouseTipsDialog.vue';

const props = defineProps({
  visit: { type: Object, default: () => ({}) },
  imovel: { type: Object, default: () => ({}) },
  agentName: { type: String, default: 'Imudei' },
});

const tenant = ref(null);
const isLoadingTenant = ref(false);
const showTips = ref(false);

const imovelId = computed(() => props.imovel?.id || props.visit?.imovelId);
const descriptor = computed(() =>
  occupiedHouseNoticeDescriptor({ ...props.visit, imovel: props.imovel })
);
const tenantContact = computed(() => {
  const attrs =
    props.imovel?.additionalAttributes ||
    props.imovel?.additional_attributes ||
    props.visit?.additionalAttributes ||
    props.visit?.additional_attributes ||
    {};
  return (
    tenant.value ||
    attrs.tenantContact ||
    attrs.tenant_contact ||
    attrs.tenant ||
    null
  );
});
const whatsappHref = computed(() =>
  buildWhatsAppUrl({
    phone: tenantContact.value?.phone || tenantContact.value?.phoneNumber,
    message: buildTalkToTenantMessage({
      tenant: tenantContact.value,
      agentName: props.agentName,
      imovel: props.imovel,
      visit: props.visit,
    }),
  })
);

const loadTenant = async () => {
  if (!descriptor.value.isTenantLiving || !imovelId.value) return;
  isLoadingTenant.value = true;
  try {
    const { data } = await ImovelAPI.tenant(imovelId.value);
    tenant.value = data?.payload || null;
  } catch (e) {
    tenant.value = null;
  } finally {
    isLoadingTenant.value = false;
  }
};

watch(() => [imovelId.value, descriptor.value.isTenantLiving], loadTenant, {
  immediate: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-root-v-if -->
  <section class="contents">
    <div
      v-if="descriptor.visible"
      class="rounded-lais border border-amber-200 bg-amber-50 p-4 text-amber-900"
    >
      <div class="flex items-start gap-3">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-full bg-white/70"
        >
          <span :class="descriptor.icon" class="size-5" aria-hidden="true" />
        </span>
        <div class="min-w-0">
          <h3 class="text-[16px] font-semibold">
            {{ descriptor.title }}
          </h3>
          <p class="mt-1 text-[13px] leading-relaxed">
            {{ descriptor.description }}
          </p>
          <p
            v-if="isLoadingTenant"
            class="mt-2 text-[12px] font-semibold text-amber-800"
          >
            Buscando dados do morador…
          </p>
        </div>
      </div>

      <div class="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-lais-sm border border-amber-300 bg-white px-3 text-[13px] font-semibold text-amber-900 transition-colors hover:border-amber-500"
          @click="showTips = true"
        >
          <span class="i-lucide-lightbulb size-4" aria-hidden="true" />
          Ver dicas
        </button>
        <a
          v-if="whatsappHref"
          :href="whatsappHref"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-lais-sm bg-lais-primary px-3 text-[13px] font-semibold text-white transition-colors hover:bg-lais-primary/90"
        >
          <span class="i-lucide-message-circle size-4" aria-hidden="true" />
          Falar com morador
        </a>
      </div>

      <OccupiedHouseTipsDialog :show="showTips" @close="showTips = false" />
    </div>
  </section>
</template>
